import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { MdShare } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../apiClient";
import ResumeSelectionPopup from "../Dashboards/CandidateDashboardpages/ResumeSelectionPopup";
import Error404 from "./Error404";
import Footer from "./Footer";

/* ------------------------------------------------------------------ */
/* Slug helper: single source of truth for job URLs                    */
/* ------------------------------------------------------------------ */
const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-") // anything that is not a letter/number -> "-"
    .replace(/^-+|-+$/g, ""); // trim leading/trailing "-"

const buildJobSlug = (job) =>
  [job.jobId, slugify(job.jobTitle), slugify(job.companyName)]
    .filter(Boolean)
    .join("-");

/* ------------------------------------------------------------------ */
/* SEO helpers (no extra dependency needed)                            */
/* ------------------------------------------------------------------ */
const upsertMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertJsonLd = (id, json) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  // Escape "<" so job text can never close the script tag
  el.textContent = json.replace(/</g, "\\u003c");
};

const removeById = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

// Returns YYYY-MM-DD for plain dates (avoids timezone shifts), full ISO otherwise.
// Returns undefined for invalid input, so it never throws.
const toSchemaDate = (value) => {
  if (!value) return undefined;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
};

// Google only accepts these employmentType values
const EMPLOYMENT_MAP = {
  "full time": "FULL_TIME",
  "full-time": "FULL_TIME",
  fulltime: "FULL_TIME",
  "part time": "PART_TIME",
  "part-time": "PART_TIME",
  parttime: "PART_TIME",
  contract: "CONTRACTOR",
  contractor: "CONTRACTOR",
  freelance: "CONTRACTOR",
  temporary: "TEMPORARY",
  temp: "TEMPORARY",
  internship: "INTERN",
  intern: "INTERN",
  volunteer: "VOLUNTEER",
  "per diem": "PER_DIEM",
};

const bufferToBase64Image = (buffer) =>
  `data:image/jpeg;base64,${btoa(
    new Uint8Array(buffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ""
    )
  )}`;

const Jobdetails = () => {
  const BASE_API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { slug } = useParams();

  // jobId is derived straight from the URL, so it can never go out of sync
  const jobId = useMemo(() => {
    const first = slug ? slug.split("-")[0] : "";
    return /^\d+$/.test(first) ? first : null;
  }, [slug]);

  const [jobDetails, setJobDetails] = useState({});
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyBanner, setCompanyBanner] = useState("");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [hasUserApplied, setHasUserApplied] = useState();
  const [applyjobs, setApplyJobs] = useState([]);
  const [showResumePopup, setShowResumePopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState(null);

  const pageError =
    error || (slug && !jobId ? "Invalid URL format: Job ID not found" : null);

  /* ---------------------------------------------------------------- */
  /* Load the job whenever the job ID in the URL changes               */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;

    // reset stale data from the previously displayed job
    setError(null);
    setJobDetails({});
    setJobs([]);
    setCompanyLogo("");
    setCompanyBanner("");
    setHasUserApplied(undefined);

    const load = async () => {
      try {
        const response = await api.getJob(jobId);
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          setJobDetails(response.data);
        } else {
          setError("Job not found");
        }
      } catch {
        if (!cancelled) setError("Failed to fetch job details");
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  /* ---------------------------------------------------------------- */
  /* Keep the URL in sync with the loaded job (title + company)        */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!jobId) return;
    if (!jobDetails?.jobTitle || !jobDetails?.companyName) return;
    // ignore a response that belongs to a different job than the URL
    if (jobDetails.jobId != null && String(jobDetails.jobId) !== String(jobId)) {
      return;
    }

    const correctSlug = buildJobSlug({ ...jobDetails, jobId });
    if (slug !== correctSlug) {
      navigate(`/browse-jobs/job-details/${correctSlug}`, { replace: true });
    }
  }, [jobDetails, jobId, slug, navigate]);

  /* ---------------------------------------------------------------- */
  /* Company assets + other jobs                                       */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const companyName = jobDetails?.companyName;
    if (!companyName) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await api.getLogo(companyName);
        if (!cancelled) setCompanyLogo(bufferToBase64Image(response.data));
      } catch (e) {}
    })();

    (async () => {
      try {
        const response = await api.getBanner(companyName);
        if (!cancelled) setCompanyBanner(bufferToBase64Image(response.data));
      } catch (e) {}
    })();

    (async () => {
      try {
        const response = await api.getLatest5JobsByCompany(companyName);
        if (!cancelled) setJobs(response.data);
      } catch (e) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [jobDetails?.companyName]);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  useEffect(() => {
    const loggedInUser = getCookie("user");
    let userObject = null;
    try {
      userObject = loggedInUser ? JSON.parse(loggedInUser) : null;
    } catch {
      userObject = null;
    }
    if (userObject && userObject.userName) {
      setIsLoggedIn(true);
      setUser(userObject);
      if (userObject.userRole === "Candidate") {
        setUserId(userObject.userId);
        setUserName(userObject.userName);
      }
    }
  }, []);

  useEffect(() => {
    if (userId && user?.userRole === "Candidate") {
      api.getResume(userId).then((res) => setResumes(res.data));
    }
  }, [userId, user]);

  useEffect(() => {
    if (!jobId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${BASE_API_URL}/applicationApplied`, {
          params: { jobId, userId },
        });
        if (!cancelled) setHasUserApplied(response.data);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, userId, BASE_API_URL]);

  const handleApplyButtonClick = (jobId) => {
    setSelectedJobId(jobId);
    setShowResumePopup(true);
  };

  const handleResumeSelect = async (resumeId) => {
    if (selectedJobId && resumeId) {
      await applyJob(selectedJobId, resumeId);
      setSelectedJobId(null);
      setShowResumePopup(false);
    }
  };

  const applyJob = async (jobId, resumeId) => {
    try {
      Swal.fire({
        title: "Applying...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const appliedOn = new Date().toISOString().split("T")[0];
      const response = await api.applyJob(jobId, userId, appliedOn, resumeId);
      if (response.data) {
        setApplyJobs((prev) => [...prev, { jobId, appliedOn }]);
        setHasUserApplied(true);
        Swal.close();
        await Swal.fire({
          icon: "success",
          title: "Apply Successful!",
          text: "You have successfully applied.",
        });
      }
    } catch {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to apply. Try again.",
      });
    }
  };
  const handleCandidateClick = () => {
    openModal("candidate");
  };
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);
  const handleModalOptionClick = (option) => {
    closeModal();
    navigate(option === "login" ? "/signin" : "/candidate-signup", {
      state: { userType: "Candidate" },
    });
  };

  const calculateDaysAgo = (date) => {
    const now = new Date();
    const past = new Date(date);
    return Math.floor((now - past) / (1000 * 3600 * 24));
  };

  const finalCompanyName = jobDetails.companyName;

  // Keep the existing banner design unchanged. Logo priority:
  // API-fetched logo -> job.companyImageUrl -> default image (display only).
  const DEFAULT_COMPANY_LOGO =
    "https://static.vecteezy.com/system/resources/previews/013/899/376/original/cityscape-design-corporation-of-buildings-logo-for-real-estate-business-company-vector.jpg";
  const displayCompanyLogo =
    companyLogo || jobDetails.companyImageUrl || DEFAULT_COMPANY_LOGO;

  /* ---------------------------------------------------------------- */
  /* Google JobPosting structured data                                 */
  /* ---------------------------------------------------------------- */
  // Canonical URL is built from the job data, not from whatever is typed in the address bar
  const canonicalUrl =
    typeof window !== "undefined" && jobId && jobDetails?.jobTitle
      ? `${window.location.origin}/browse-jobs/job-details/${buildJobSlug({
          ...jobDetails,
          jobId,
        })}`
      : "";

  // Expired jobs must not carry active JobPosting markup
  const deadlineDate = jobDetails?.applicationDeadline
    ? new Date(jobDetails.applicationDeadline)
    : null;
  const isExpired =
    deadlineDate &&
    !isNaN(deadlineDate.getTime()) &&
    deadlineDate.getTime() + 24 * 60 * 60 * 1000 < Date.now();

  const employmentType =
    EMPLOYMENT_MAP[
      String(jobDetails?.jobType || "")
        .toLowerCase()
        .trim()
    ];

  // Only a real, crawlable https logo (never the base64 display logo)
  const schemaLogo = [
    jobDetails?.company?.logo,
    jobDetails?.companyLogo,
    jobDetails?.companyImageUrl,
  ].find(isHttpUrl);

  const jobPostingSchema =
    jobDetails?.jobTitle &&
    jobDetails?.companyName &&
    jobDetails?.jobsummary &&
    jobDetails?.location &&
    !isExpired
      ? {
          "@context": "https://schema.org",
          "@type": "JobPosting",
          title: jobDetails.jobTitle,
          description: jobDetails.jobsummary,
          identifier: {
            "@type": "PropertyValue",
            name: jobDetails.companyName,
            value: String(jobDetails.jobId ?? jobId),
          },
          datePosted: toSchemaDate(jobDetails.postingDate),
          validThrough: toSchemaDate(jobDetails.applicationDeadline),
          employmentType,
          directApply: true,
          hiringOrganization: {
            "@type": "Organization",
            name: jobDetails.companyName,
            ...(schemaLogo ? { logo: schemaLogo } : {}),
          },
          jobLocation: {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressLocality: jobDetails.location,
              addressCountry: jobDetails.country || "IN",
            },
          },
          url: canonicalUrl,
        }
      : null;

  // undefined values are dropped by JSON.stringify
  const schemaJson = jobPostingSchema ? JSON.stringify(jobPostingSchema) : "";

  // Put JSON-LD, title, description and canonical in <head>
  useEffect(() => {
    if (pageError) {
      upsertMeta("robots", "noindex");
      removeById("jobposting-jsonld");
      return;
    }
    if (jobDetails?.jobTitle && jobDetails?.companyName) {
      document.title = `${jobDetails.jobTitle} at ${jobDetails.companyName} | Jobbox.one`;
      const summary = String(jobDetails.jobsummary || "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 155);
      if (summary) upsertMeta("description", summary);
      if (canonicalUrl) upsertCanonical(canonicalUrl);
      upsertMeta("robots", isExpired ? "noindex" : "index, follow");
    }
    if (schemaJson) {
      upsertJsonLd("jobposting-jsonld", schemaJson);
    } else {
      removeById("jobposting-jsonld");
    }
    return () => removeById("jobposting-jsonld");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schemaJson,
    pageError,
    canonicalUrl,
    jobDetails?.jobTitle,
    jobDetails?.companyName,
  ]);

  // All hooks are above this line; early returns must come after them
  if (pageError) return <Error404 />;

  const handleShare = (job) => {
    const shareUrl = `${window.location.origin}/browse-jobs/job-details/${buildJobSlug(job)}`;

    if (navigator.share) {
      navigator
        .share({
          title: "Job Opportunity",
          text: "Check out this job!",
          url: shareUrl,
        })
        .then(() => console.log("Job shared successfully"))
        .catch((error) => console.error("Error sharing", error));
    } else {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          Swal.fire({
            title: "Link Copied!",
            text: "Job link copied to clipboard. You can share it now.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch(() => {
          Swal.fire({
            title: "Error",
            text: "Could not copy link. Please try again.",
            icon: "error",
          });
        });
    }
  };

  return (
    <div>
      <div style={{ overflow: "hidden" }}>
        <div style={{ overflow: "hidden" }}>
          {" "}
          <div
            className="welcome-msg"
            style={{ overflowY: "scroll", overflowX: "hidden" }}
          >
            <Row>
              <div>
                <Card
                  style={{
                    width: "100%",
                    height: "60%",
                    borderRadius: "10px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <Card.Body style={{ padding: 0, position: "relative" }}>
                    <div style={{ position: "relative", height: "55%" }}>
                      <img
                        src={
                          companyBanner ||
                          "https://cdn.pixabay.com/photo/2016/04/20/07/16/logo-1340516_1280.png"
                        }
                        alt="Company Banner"
                        className="banner-image"
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "200px",
                          objectFit: "cover",
                          cursor: "pointer",
                          borderTopLeftRadius: "10px",
                          borderTopRightRadius: "10px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        top: "90%",
                        left: "50px",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <img
                        src={displayCompanyLogo}
                        onError={(event) => {
                          if (
                            event.currentTarget.src !== DEFAULT_COMPANY_LOGO
                          ) {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = DEFAULT_COMPANY_LOGO;
                          }
                        }}
                        alt={`${finalCompanyName || "Company"} Logo`}
                        className="logo-image"
                        style={{
                          width: "20vw",
                          height: "20vw",
                          maxWidth: "150px",
                          maxHeight: "150px",
                          cursor: "pointer",
                          objectFit: "cover",
                          clipPath: "none",
                          border: "5px solid #f0f0f0",
                          borderRadius: "10px",
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Row>
            <h2 className="responsive-title">
              <b>{finalCompanyName}</b>
            </h2>
            <hr style={{ width: "100%", backgroundColor: "grey" }}></hr>
            <div style={{ width: "100%", marginTop: "30px" }}>
              <Row>
                <Col lg={9} style={{ height: "fit-content" }}>
                  <Card
                    style={{
                      top: "10%",
                      width: "100%",
                      height: "fit-content",
                    }}
                  >
                    <Card.Header>
                      <Card.Title
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          height: "auto",
                          padding: "0px",
                        }}
                      >
                        <h2 className="text-start">Job Description</h2>
                        <div className="d-flex align-items-center">
                          {isLoggedIn ? (
                            user?.userRole === "Candidate" ? (
                              hasUserApplied ? (
                                <p
                                  style={{
                                    color: "#28a745",
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    backgroundColor: "#e9f5e9",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    textAlign: "left",
                                    margin: "0",
                                  }}
                                >
                                  Applied
                                </p>
                              ) : (
                                <Button
                                  style={{ margin: 0 }}
                                  onClick={() => handleApplyButtonClick(jobId)}
                                >
                                  Apply
                                </Button>
                              )
                            ) : (
                              <p
                                style={{
                                  color: "#dc3545",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  backgroundColor: "#f8d7da",
                                  padding: "5px 10px",
                                  borderRadius: "5px",
                                  textAlign: "left",
                                  margin: "0",
                                }}
                              >
                                Only candidates can apply
                              </p>
                            )
                          ) : (
                            <Button
                              style={{
                                margin: 0,
                                backgroundColor: "#30c4e9ff",
                              }}
                              variant="light"
                              onClick={handleCandidateClick}
                            >
                              Login to Apply
                            </Button>
                          )}

                          <div
                            className="d-flex align-items-center text-success ms-3"
                            title="Share this job"
                            onClick={() =>
                              handleShare({ ...jobDetails, jobId })
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <MdShare size={20} className="me-1" />
                            <span>Share</span>
                          </div>
                        </div>
                      </Card.Title>
                      <Card.Body>
                        <p>
                          <strong>Company Name : </strong>
                          {jobDetails.companyName}
                        </p>
                        <p>
                          <strong>Job Title:</strong> {jobDetails.jobTitle}
                        </p>
                        <p>
                          <strong>Job Type:</strong> {jobDetails.jobType}
                        </p>
                        <p>
                          <strong>Skills:</strong> {jobDetails.skills}
                        </p>
                        <p>
                          <strong>Posting Date:</strong>{" "}
                          {jobDetails.postingDate}
                        </p>
                        <p>
                          <strong>Number of Positions:</strong>{" "}
                          {jobDetails.numberOfPosition}
                        </p>
                        <p>
                          <strong>Salary:</strong> {jobDetails.salary}
                        </p>
                        <p>
                          <strong>Location:</strong> {jobDetails.location}
                        </p>
                        <strong>Job Summary:</strong>{" "}
                        <pre className="job-details-text">
                          {jobDetails.jobsummary}
                        </pre>
                        <p>
                          <strong>Application Deadline:</strong>
                          {jobDetails.applicationDeadline ? (
                            jobDetails.applicationDeadline
                          ) : (
                            <span
                              style={{ color: "green", fontWeight: "bold" }}
                            >
                              Evergreen Job
                            </span>
                          )}
                        </p>
                      </Card.Body>
                    </Card.Header>
                  </Card>
                </Col>

                <Col lg={3}>
                  <div
                    className="other-jobs"
                    style={{
                      margin: "8px",
                      width: "250px",
                      height: "fit-content",
                    }}
                  >
                    <h3
                      style={{
                        marginTop: "20px",
                        marginLeft: "12px",
                        fontSize: "18px",
                        color: "purple",
                      }}
                    >
                      Other Jobs
                    </h3>
                    {jobs.map((job) => (
                      <Card className="other-jobs" key={job.jobId}>
                        <Card.Body
                          onClick={() => {
                            const fullUrl = `${window.location.origin}/browse-jobs/job-details/${buildJobSlug(job)}`;
                            window.open(
                              fullUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          style={{ padding: "8px" }}
                          title={
                            job.jobCategory === "evergreen"
                              ? "This position is always open for hiring, feel free to apply anytime!"
                              : ""
                          }
                        >
                          <Card.Title style={{ fontSize: "14px" }}>
                            {job.jobTitle} ({job.jobType})
                          </Card.Title>

                          <Card.Text style={{ fontSize: "12px" }}>
                            <strong>Application Deadline:</strong>
                            {job.applicationDeadline ? (
                              job.applicationDeadline
                            ) : (
                              <span
                                style={{ color: "green", fontWeight: "bold" }}
                              >
                                Evergreen Job
                              </span>
                            )}
                            <br />
                            Posted {calculateDaysAgo(job.postingDate)} days ago
                            <br />
                          </Card.Text>
                          <hr style={{ backgroundColor: "black" }} />
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>
            <Footer />

            {showResumePopup && (
              <ResumeSelectionPopup
                resumes={resumes}
                onSelectResume={handleResumeSelect}
                show={true}
                onClose={() => setShowResumePopup(false)}
                userName={userName}
                userId={userId}
              />
            )}

            <Modal show={showModal} onHide={closeModal}>
              <Modal.Header
                closeButton
                style={{
                  backgroundColor: "#faccc",
                  color: "white",
                  borderBottom: "none",
                }}
              >
                <Modal.Title>Choose an Option</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "20px", textAlign: "center" }}>
                <Button
                  variant="primary"
                  onClick={() => handleModalOptionClick("login")}
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    backgroundColor: "#6c5ce7",
                    borderColor: "#6c5ce7",
                  }}
                >
                  Already have an account - Login
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleModalOptionClick("register")}
                  style={{
                    width: "100%",
                    backgroundColor: "#00b894",
                    borderColor: "#00b894",
                  }}
                >
                  Don't have an account - Register
                </Button>
              </Modal.Body>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Jobdetails;